import * as HLF from "html-living-framework";
import { MathUtils } from "html-living-framework";
import {Ease} from "../core/Animatable";
import {IPoint, PointUtil} from "../core/Geom";
import {Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {AbstractCharacter} from "./AbstractCharacter";
import { Oscar } from "./Oscar";
import {TrashItem} from "./TrashItem";
import { TrashItemCollect } from "./TrashItemCollect";
import { TrashItemReturn } from "./TrashItemReturn";

// keep oscar on path
const minY: number = 480;
const maxY: number = 610;

export class OscarCollect extends Oscar {

    get collectedItems(): TrashItem[] {
        return this._collectedItems;
    }

    protected readonly _itemsToCollect: TrashItemCollect[] = [];
    protected _lastMousePos?: PIXI.Point;

    private _isMoving = false;

    public onLayout() {
        super.onLayout();
        this.setState(OscarState.Idle);

        this.onMouseMoved = this.onMouseMoved.bind(this);
        const interactionManager: PIXI.InteractionManager = HLF.Application.instance.renderer.plugins.interaction;
        interactionManager.addListener(HLF.Input.Events.POINTER_MOVE, this.onMouseMoved);
        interactionManager.addListener(HLF.Input.Events.POINTER_DOWN, this.onMouseMoved);

        this.spine.state.setAnimation(this.tracks.MOTOR, "vaccum-running", true);

        this.slimyClickTarget.interactive = true;
        this.slimyClickTarget.buttonMode = true;

        this.nozzleClickTarget.interactive = true;
        this.nozzleClickTarget.buttonMode = false;
    }

    public collectItem(item: TrashItemCollect, cb?: Callback) {
        if (this._isMoving) {
            // Only 1 item is allowed in the queue
            if (this._itemsToCollect.length === 0) {
                this._itemsToCollect.push(item);
                item.QueuedForPickup();
            }
            else {
                item.QueuedPickupCancelled();
            }
        }

        this.setState(OscarState.Collecting);
        const nozzleXOffset = 500;
        const nozzleYOffset = -400;

        this.spine.state.setAnimation(this.tracks.NOZZLE, "air-suck", false);
        this.spine.state.addEmptyAnimation(this.tracks.NOZZLE, 0, 0);

        HLF.Application.instance.audio.play("trash-blaster-suction", 2, false, HLF.AudioCategory.SFX);

        item.PickUp({x: this.x + (nozzleXOffset * Math.sign(this.scale.x)), y: this.y + nozzleYOffset}, () => {
            this._collectedItems.push(item);

            const bulgeAnim = this.spine.state.setAnimation(this.tracks.HOSE, "bulge", false);

            const bagFillAnim = this.spine.state.addAnimation(this.tracks.BAG, "bag-fill", false, bulgeAnim.animationEnd);
            bagFillAnim.time = (this.collectedItems.length / 6) * bagFillAnim.animationEnd;
            bagFillAnim.animationEnd = ((this.collectedItems.length + 1) / 6) * bagFillAnim.animationEnd;

            if (this._itemsToCollect.length > 0) {
                const nextItem = this._itemsToCollect.shift()!;
                this.collectItem(nextItem, cb);
            }
            else {
                // no more items in queue
                if (cb) {
                    cb();
                }
            }
        });

        item.QueuedForPickup();
    }

    public moveTo(pPoint: IPoint | TrashItemCollect, cb?: Callback) {
        if (this._isMoving) {
            return false;
        }
        this._isMoving = true;
        const speed = 800;
        const minPickupDistance = 500;
        const maxPickupDistance = 1524;

        const yOffset = 200;

        // tslint:disable-next-line: prefer-const
        let {x, y} = pPoint;

        if (pPoint instanceof TrashItemCollect) {
            if (pPoint.x < this.x/*  + minPickupDistance */) { // move left
                x = pPoint.x + minPickupDistance;
                this.scale.x = -1 * Math.abs(this.scale.x);
            }
            else { // move right or stay still
                x = Math.max(this.x, pPoint.x - maxPickupDistance);
                this.scale.x = Math.abs(this.scale.x);
            }

            // don't move up unless we have to
            if (pPoint.y < this.y && pPoint.y > this.y - yOffset) {
                y = this.y;
            }
        }

        y = HLF.MathUtils.clamp(y, minY, maxY);

        this.moveYTo(y, PointUtil.distance(this, {x, y}) / speed, Ease.sineInOut);

        this.walkTo(x, speed, Ease.sineInOut, () => {
            this._isMoving = false;
            if (cb) {
                cb();
            }
        });

        return true;
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        if (this._lastMousePos) {
            const hoseBone = this.spine.skeleton.findBone("right-arm-hose-target");
            const newBonePos = hoseBone.parent.worldToLocal(new PIXI.spine.core.Vector2(
                Math.max(this._lastMousePos.x, 1000),
                this._lastMousePos.y,
            ));
            const ik = this.spine.skeleton.findIkConstraint("right-arm");
            ik.stretch = false;
            hoseBone.data.x = newBonePos.x;
            hoseBone.data.y = newBonePos.y;

            const nozzleBone = this.spine.skeleton.findBone("nozzle");
            const nozToMouseDist = {
                x: this._lastMousePos.x - nozzleBone.worldX,
                y: nozzleBone.worldY - this._lastMousePos.y,
            };

            if (this._lastMousePos.y < -this.height / 2) {
                if (this.spine.state.getCurrent(this.tracks.ARMS)?.animation?.name !== "hose-curve-up") {
                    this.spine.state.setAnimation(this.tracks.ARMS, "hose-curve-up", false);
                    // HLF.Application.instance.audio.play("trash-blaster-hose-creak");
                }
            }
            else {
                if (this.spine.state.getCurrent(this.tracks.ARMS)?.animation?.name !== "hose-curve-down") {
                    this.spine.state.setAnimation(this.tracks.ARMS, "hose-curve-down", false);
                    // HLF.Application.instance.audio.play("trash-blaster-hose-creak");
                }
            }

            const nozRot = (Math.atan2(nozToMouseDist.x, nozToMouseDist.y) * PIXI.RAD_TO_DEG) + 90;
            nozzleBone.data.rotation = MathUtils.clamp(nozzleBone.parent.worldToLocalRotation(nozRot), -10, 0);
        }
    }

    public wait() {
        this.setState(OscarState.Idle);
    }

    public destroy() {
        super.destroy();
        const interactionManager: PIXI.InteractionManager = HLF.Application.instance.renderer.plugins.interaction;
        interactionManager.removeListener(HLF.Input.Events.POINTER_MOVE, this.onMouseMoved);
        interactionManager.removeListener(HLF.Input.Events.POINTER_DOWN, this.onMouseMoved);
    }

    protected setState(state: OscarState) {
        switch (state) {
            case OscarState.Collecting:
                break;
        }
        // this.oscarNormal.visible = state === OscarState.Idle;
        // this.oscarWalking.visible = state === OscarState.Walking || state === OscarState.Collecting;
    }

    protected onMouseMoved(pData: PIXI.InteractionEvent) {
        this._lastMousePos = pData.data.getLocalPosition(this.spine);
    }
}

enum OscarState {
    Idle,
    Walking,
    Collecting,
}
